import { UserSuggestedUpdateType } from '@bb/users';
import { UserSuggestedUpdateController } from 'api';
import type { IUserSuggestedUpdateGetPendingSuggestionsResponse } from '@/app-package';
import AuthContext from 'contexts/AuthContext';
import React, { useCallback, useContext, useEffect } from 'react';
import OnboardingWelcomeModalContext from './OnboardingWelcomeModalContext';

export type UserSuggestedUpdateModalContextType = {
  visible: boolean;
  suggestedUpdates: IUserSuggestedUpdateGetPendingSuggestionsResponse | null;
  openModal: () => void;
  closeModal: () => void;
};

const USER_UPDATE_TYPES = [
  UserSuggestedUpdateType.email,
  UserSuggestedUpdateType.names,
];

const UserSuggestedUpdateModalContext =
  React.createContext<UserSuggestedUpdateModalContextType>(null);

export const UserSuggestedUpdateModalContextProvider: React.FC = ({
  children,
}) => {
  const { auth } = useContext(AuthContext);
  const { isDoneWelcomeFlow } = useContext(OnboardingWelcomeModalContext);

  const [visible, setVisible] = React.useState(false);
  const [suggestedUpdates, setSuggestedUpdates] =
    React.useState<IUserSuggestedUpdateGetPendingSuggestionsResponse | null>(
      null,
    );

  const openModal: UserSuggestedUpdateModalContextType['openModal'] =
    useCallback(() => {
      setVisible(true);
    }, []);

  const closeModal: UserSuggestedUpdateModalContextType['closeModal'] =
    useCallback(() => {
      setVisible(false);
    }, []);

  useEffect(() => {
    if (!auth?.id || !isDoneWelcomeFlow) return;
    let active = true;

    const getSuggestedUpdates = async () => {
      const pendingSuggestedUpdates =
        await UserSuggestedUpdateController.getPendingSuggestions(
          { userId: auth.id },
          { type: [...USER_UPDATE_TYPES] },
        );
      if (!active || !pendingSuggestedUpdates.length) return;
      setSuggestedUpdates(pendingSuggestedUpdates);
    };
    getSuggestedUpdates();

    return () => {
      active = false;
    };
  }, [auth?.id, isDoneWelcomeFlow]);

  return (
    <UserSuggestedUpdateModalContext.Provider
      value={{ visible, suggestedUpdates, openModal, closeModal }}
    >
      {children}
    </UserSuggestedUpdateModalContext.Provider>
  );
};

export default UserSuggestedUpdateModalContext;
